import { motion } from "framer-motion";
import {
  Bell, AtSign, CalendarClock, CalendarCheck2, ClipboardList, MessageSquare, X, Megaphone, ShieldCheck,
  UserPlus, UserMinus, FolderKanban, LifeBuoy, ListChecks, Heart, KeyRound, Plane,
} from "lucide-react";
import { timeAgo } from "@/lib/constants";

const TYPES = {
  assignment: { icon: ClipboardList, color: "#7FB8FF", label: "Assigned" },
  reply: { icon: MessageSquare, color: "#00FF66", label: "Reply" },
  mention: { icon: AtSign, color: "#C3A6FF", label: "Mention" },
  reaction: { icon: Heart, color: "#FF8BC1", label: "Reaction" },
  review: { icon: ShieldCheck, color: "#FFB020", label: "Review" },
  announcement: { icon: Megaphone, color: "#FF8B94", label: "Announcement" },
  pto: { icon: Plane, color: "#A8E6CF", label: "Time off" },
  pto_request: { icon: CalendarClock, color: "#FFD3B6", label: "Time off" },
  pto_decision: { icon: CalendarCheck2, color: "#A8E6CF", label: "Time off" },
  pto_booked: { icon: CalendarCheck2, color: "#A8E6CF", label: "Time off" },
  pto_cover: { icon: LifeBuoy, color: "#5EEAD4", label: "Cover" },
  todo_reminder: { icon: ListChecks, color: "#FFE066", label: "Reminder" },
  joined: { icon: UserPlus, color: "#00FF66", label: "Team" },
  left: { icon: UserMinus, color: "#FF8B94", label: "Team" },
  project: { icon: FolderKanban, color: "#7FB8FF", label: "Project" },
  role: { icon: KeyRound, color: "#C3A6FF", label: "Access" },
};
const typeOf = (t) => TYPES[t] || { icon: Bell, color: "#90C2E7", label: "Update" };

// One row in the notifications panel: colored type icon, unread accent, hover-only dismiss.
export function NotificationItem({ n, index, onOpen, onDismiss }) {
  const { icon: Icon, color, label } = typeOf(n.type);
  return (
    <motion.li
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 24 }}
      transition={{ duration: 0.22, delay: Math.min(index, 8) * 0.025, ease: [0.22, 1, 0.36, 1] }}
      className="group relative"
    >
      <button
        onClick={() => onOpen(n)}
        className={`relative flex w-full items-start gap-3.5 rounded-xl px-3.5 py-3 pr-10 text-left transition-colors duration-150 ${n.read ? "hover:bg-white/[0.03]" : "bg-white/[0.035] hover:bg-white/[0.06]"}`}
        data-testid={`notification-item-${n.id}`}
      >
        {!n.read && <span className="absolute inset-y-3 left-0 w-[3px] rounded-full" style={{ backgroundColor: color, boxShadow: `0 0 10px ${color}` }} />}
        <span
          className={`relative mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1 transition-opacity ${n.read ? "opacity-60" : ""}`}
          style={{ backgroundColor: `${color}14`, color, "--tw-ring-color": `${color}33` }}
        >
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-[9px] font-black uppercase tracking-[0.16em]" style={{ color: n.read ? "rgba(255,255,255,0.3)" : color }}>{label}</span>
            <span className="text-[10px] text-white/25">·</span>
            <span className="text-[10px] font-medium text-white/35" data-testid={`notification-time-${n.id}`}>{timeAgo(n.created_at)}</span>
            {n.foreign && n.workspace_name && (
              <span className="ml-auto truncate rounded-full bg-[#FFB020]/12 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#FFB020]" data-testid={`notification-workspace-${n.id}`}>
                {n.workspace_name}
              </span>
            )}
          </span>
          <span className={`mt-1 block text-[13px] font-semibold leading-snug ${n.read ? "text-white/55" : "text-white"}`}>{n.title}</span>
          {n.body && <span className={`mt-1 line-clamp-2 break-words text-xs leading-relaxed ${n.read ? "text-white/30" : "text-white/55"}`}>{n.body}</span>}
          {n.actor_name && <span className="mt-1.5 block truncate text-[10px] font-medium text-white/30">{n.actor_name}</span>}
        </span>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onDismiss(n); }}
        className="absolute right-2.5 top-3 rounded-lg p-1.5 text-white/40 transition-[opacity,color,background-color] duration-150 hover:bg-white/10 hover:text-[#FF8B94] sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100"
        data-testid={`notification-dismiss-${n.id}`}
        aria-label="Dismiss notification"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </motion.li>
  );
}
